<template>
<!-- eslint-disable max-len -->
<div
  :style="{
    'z-index': (order ? order : 0),
    'pointer-events': (char.clickToExpandImage ? undefined : 'none'),
  }"
  class="narrator"
  :class="{ full, left, sf: (starfighter) }"
>
  <template v-if="!useView360">
    <img
      v-show="use1"
      :src="narratorImgSrc"
      :style="style"
      draggable="false"
      @load="onLoaded"
      @click="onClicked"
    >
    <img
      v-show="!use1"
      :src="narratorImgSrc2"
      :style="style2"
      draggable="false"
      @load="onLoaded"
      @click="onClicked"
    >
  </template>
  <View360
    v-else
    class="override-view360"
    auto-resize
    :style="computedStyle"
    :projection="projectedNarratorImg"
    canvas-class="gl-canvas"
    @input-start="onClicked"
  />
  <tooltip :target="tooltipTarget"></tooltip>
  <b-modal
    v-if="char"
    v-model="isImageExpanded"
    modal-class="override-bmodal"
    ok-only
    :size="char.expandedImageModalWidth || 'lg'"
  >
    <img
      v-if="!useExpandedView360"
      style="width: 100%; object-fit: contain;"
      :style="{
        'max-height': char.expandedImageMaxHeightOverride || '72vh',
      }"
      :src="expandedNarratorImg"
    />
    <View360
      v-else-if="showModalView360"
      class="override-view360"
      style="width: 100%; object-fit: contain; overflow: hidden;"
      :style="{
        'max-height': `min((100vh - 60px), ${char.expandedImageMaxHeightOverride || '72vh'})`,
      }"
      auto-resize
      :projection="projectedExpandedNarratorImg"
      canvas-class="gl-canvas"
    />
  </b-modal>
</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { View360, EquirectProjection, CylindricalProjection } from '@egjs/vue-view360';

import Tooltip from './Tooltip';

export default {
  components: {
    Tooltip,
    View360,
  },
  props: {
    character: { type: Object },
    scale: { type: Number, default: 1 },
    full: { type: Boolean },
    left: { type: Boolean },
    variantOverride: { type: String, default: '' },
    xOffset: { type: String, default: '' },
    yOffset: { type: String, default: '' },
    orderOverride: { type: Number, default: 0 },
    tooltipTarget: { type: String, default: 'characterRight' },
  },
  data: () => ({
    narratorImgSrc: '',
    narratorImgSrc2: '',
    style: {},
    style2: {},
    srcConfirmedLoaded: false,
    use1: false,
    isImageExpanded: false,
    showModalView360: false,
  }),
  computed: {
    ...mapGetters([
      'narrator',
      'narrative',
      'game',
      'starfighter',
      'native'
    ]),
    char() {
      return this.character || this.narrator;
    },
    variant() {
      let variantName = this.variantOverride;

      if (
        !(variantName) &&
        this.narrative != null &&
        this.char.imageVariants &&
        this.char.imageVariants.length > 0
      ) {
        if (this.left && this.narrative.leftVariant) {
          variantName = this.narrative.leftVariant;
        } else if (!(this.left) && this.narrative.rightVariant) {
          variantName = this.narrative.rightVariant;
        }
      }

      if (variantName && this.char.imageVariants != null) {
        const variant = this.char.imageVariants.find(v => v.name === variantName);
        return variant;
      }

      return null;
    },
    imageVariant() {
      if (this.variant) return this.variant.image;

      return null;
    },
    narratorImg() {
      let { image = 'TM_character00a.png' } = this.char;
      if (this.imageVariant) {
        image = this.imageVariant;
      }
      return this.resolveAssetPath(image, 'characters');
    },
    useView360() {
      return this.char && this.char.view360;
    },
    view360Cylindrical() {
      return this.char && this.char.view360ProjectionType === 'Cylindrical';
    },
    projectedNarratorImg() {
      if (this.useView360) {
        return this.view360Cylindrical
          ? new CylindricalProjection({
            src: this.narratorImg,
          })
          : new EquirectProjection({
            src: this.narratorImg,
          });
      }
      return (this.use1 ? this.narratorImgSrc : this.narratorImgSrc2);
    },
    expandedNarratorImg() {
      return (this.char && this.char.expandedImage)
        ? this.resolveAssetPath(this.char.expandedImage, 'characters')
        : this.narratorImg;
    },
    useExpandedView360() {
      return this.char
        && ((this.char.expandedImage && this.char.expandedView360)
          || (!(this.char.expandedImage) && this.char.view360));
    },
    expandedView360Cylindrical() {
      return this.char && this.char.expandedView360ProjectionType === 'Cylindrical';
    },
    projectedExpandedNarratorImg() {
      if (this.useExpandedView360) {
        if (this.useView360 && !(this.char && this.char.expandedImage)) {
          return this.projectedNarratorImg;
        }
        return this.expandedView360Cylindrical
          ? new CylindricalProjection({
            src: this.expandedNarratorImg,
          })
          : new EquirectProjection({
            src: this.expandedNarratorImg,
          });
      }
      return this.expandedNarratorImg;
    },
    order() {
      if (this.orderOverride) {
        return this.orderOverride;
      }
      if (this.narrative == null) {
        return 0;
      }
      return (this.left) ? this.narrative.leftOrderOverride : this.narrative.rightOrderOverride;
    },
    leftXOffset() {
      if (this.xOffset || this.narrative == null) {
        return this.xOffset;
      }
      return this.narrative.leftXOffset;
    },
    rightXOffset() {
      if (this.xOffset || this.narrative == null) {
        return this.xOffset;
      }
      return this.narrative.rightXOffset;
    },
    leftYOffset() {
      if (this.yOffset || this.narrative == null) {
        return this.yOffset;
      }
      return this.narrative.leftYOffset;
    },
    rightYOffset() {
      if (this.yOffset || this.narrative == null) {
        return this.yOffset;
      }
      return this.narrative.rightYOffset;
    },
    computedStyle() {
      let rightVal;
      let leftVal;
      if (this.left) {
        leftVal = (this.variant != null)
          ? this.variant.xOffset
          : (this.char != null ? this.char.xOffset : undefined);
        if (this.leftXOffset) {
          leftVal = leftVal ? `calc(${leftVal} + ${this.leftXOffset})` : this.leftXOffset;
        }
      } else {
        rightVal = (this.variant != null)
          ? this.variant.xOffset
          : (this.char != null ? this.char.xOffset : undefined);
        if (this.rightXOffset) {
          rightVal = rightVal ? `calc(${rightVal} + ${this.rightXOffset})` : this.rightXOffset;
        }
      }
      let bottomVal = (this.variant != null && this.variant.yOffset)
        ? this.variant.yOffset
        : ((this.variant == null && this.char != null && this.char.yOffset)
          ? this.char.yOffset
          : undefined);
      if (this.useView360) {
        bottomVal = bottomVal ? `${bottomVal} + 575px` : '575px';
      }
      if (this.left && this.leftYOffset) {
        bottomVal = bottomVal ? `calc(${bottomVal} + ${this.leftYOffset})` : this.leftYOffset;
      } else if (!(this.left) && this.rightYOffset) {
        bottomVal = bottomVal ? `calc(${bottomVal} + ${this.rightYOffset})` : this.rightYOffset;
      }
      return {
        'pointer-events': (this.char && this.char.clickToExpandImage ? undefined : 'none'),
        cursor: (this.char && this.char.clickToExpandImage) ? 'pointer' : undefined,
        transform: `scale(${this.scale * ((!(this.left) && this.variant == null) ? -1 : 1)}, ${this.scale})`,
        position: 'relative',
        right: rightVal,
        left: leftVal,
        bottom: bottomVal,
        width: this.useView360 ? (this.char.view360ViewportWidth || '950px') : undefined,
      };
    }
  },
  watch: {
    narratorImg(newValue) {
      // console.log(`${this.tooltipTarget} - narratorImg change`);
      this.srcConfirmedLoaded = false;
      if (this.use1) {
        if (this.narratorImgSrc2 === newValue) this.onLoaded();
        else this.narratorImgSrc2 = newValue;
      } else {
        // eslint-disable-next-line no-lonely-if
        if (this.narratorImgSrc === newValue) this.onLoaded();
        else this.narratorImgSrc = newValue;
      }
    },
    computedStyle(newValue) {
      // console.log(`${this.tooltipTarget} - computedStyle change`);
      // console.log(this.srcConfirmedLoaded);
      // console.log(this.use1);
      if (this.srcConfirmedLoaded) {
        if (this.use1) this.style = newValue;
        else this.style2 = newValue;
      } else {
        // eslint-disable-next-line no-lonely-if
        if (this.use1) this.style2 = newValue;
        else this.style = newValue;
      }
    },
  },
  methods: {
    onLoaded() {
      // console.log(`${this.tooltipTarget} - onLoaded`);
      this.srcConfirmedLoaded = true;
      if (this.use1) this.style2 = this.computedStyle;
      else this.style = this.computedStyle;
      this.use1 = !(this.use1);
    },
    onClicked() {
      if (this.char && this.char.clickToExpandImage) {
        this.isImageExpanded = true;
        this.showModalView360 = false;
        setTimeout(() => {
          this.showModalView360 = true;
        }, 200);
      }
    }
  },
  async mounted() {
    // console.log('narratorImg init');
    this.narratorImgSrc = this.narratorImg;
    // console.log('style init');
    this.style = this.computedStyle;
  }
};
</script>

<style lang="scss" scoped>
.narrator {
  position: absolute;
  // transition: bottom 1s;

  // new position parameters
  right: -174px;
  top: 0;
  height: 1950px;
}
.narrator.sf {
  right: -10px;
  height: unset !important;
  top: unset !important;
  bottom: -280px !important;
}
.narrator img {
  max-height: 100%;
  object-fit: contain;
}
// .narrator.full {
//   bottom: -280px;
// }
.narrator.left {
  right: unset;

  // new position parameter
  left: -174px;
}
.narrator.left.sf {
  // old position parameter
  left: -60px;
}

/deep/ .override-bmodal > .modal-fullscreen {
  width: 95vw !important;
  max-width: unset !important;
  height: fit-content !important;
  max-height: calc(100vh - 60px) !important;
}

/deep/ .override-view360 > .gl-canvas {
  width: 100% !important;
  height: 100% !important;
}
</style>



// WEBPACK FOOTER //
// src/components/game/ui/Narrator.vue